import { Injectable, NgZone } from '@angular/core';
import { Router } from '@angular/router';
import { ModalService } from './modal.service';
import { ToastService } from '@common/services/toast.service';
import { SecureStorageService } from './../services/secure-storage.service'
import { CookieService } from 'ngx-cookie-service';

@Injectable({
  providedIn: 'root',
})
export class IdelTimeoutService {
  private idleTimeout: any;
  private logoutTimeout: any;
  private readonly timeoutDuration = 60 * 60 * 1000; // 1 hour
  private readonly warningDuration = 5 * 60 * 1000; // 5 minutes
  private modalRef: any; // Reference to the active modal
  private eventListeners: string[] = ['mousemove', 'keydown', 'click', 'scroll']; // Events to track
  private isLoggedOut = false; // Prevent multiple logout calls
  private countdownTimeout: any; // For the countdown timer
  private countdown: number = this.warningDuration / 1000; // Countdown in seconds
  private readonly resetTimerHandler = () => this.resetTimer();

  constructor(
    private modalService: ModalService,
    private router: Router,
    private ngZone: NgZone,
    private toastService: ToastService,
    private secureStorage: SecureStorageService,
    private cookieService:CookieService
  ) {}

  startTracking(): void {
    this.resetTimer();

    // Add event listeners for user activity
    this.eventListeners.forEach(event => {
      window.addEventListener(event, this.resetTimerHandler);
    });
  }

  resetTimer(): void {
    if (this.isLoggedOut) return; // Don't reset the timer if the user is logged out

    clearTimeout(this.idleTimeout);
    clearTimeout(this.logoutTimeout); // Clear auto-logout timer when activity is detected
    clearTimeout(this.countdownTimeout);

    // Set timer for inactivity
    this.idleTimeout = setTimeout(() => this.showWarning(), this.timeoutDuration - this.warningDuration);
  }

  private async showWarning(): Promise<void> {
    if (this.isLoggedOut) return; // Don't show warning if the user is logged out
    
    const title = 'Session Expire Warning';
    const message =
      'Your session will expire in 5:00 minutes. Do you want to extend the session?';
  
    // Reset countdown
    this.countdown = this.warningDuration / 1000;
    const activeModalRef = this.modalService.openInactivityAlert(
      title,
      message,
      this.formatCountdown(this.countdown) // Pass formatted countdown initially
    );
    this.modalRef = activeModalRef;
  
    // Ensure modalRef is initialized
    console.log('ModalRef initialized:', this.modalRef);
  
    // Recursively call setTimeout to update countdown
    const updateCountdown = () => {
      if (
        this.isLoggedOut ||
        this.modalRef !== activeModalRef ||
        !activeModalRef.componentInstance
      ) {
        return;
      }

      if (this.countdown <= 0) {
        this.logoutUser(); // Log out when countdown hits 0
        return;
      }
  
      // Decrement countdown
      this.countdown--;

        // Show the countdown only when there are 5 seconds or less
    if (this.countdown <= 300) {
      let updatedMessage;
      if(this.countdown <= 60){
         updatedMessage = `Your session will expire in ${this.formatCountdown(this.countdown)} seconds. Do you want to extend the session?`;
      }else{
        updatedMessage = `Your session will expire in ${this.formatCountdown(this.countdown)} minutes. Do you want to extend the session?`;
      }
      //const updatedMessage = `Auto logout in ${this.formatCountdown(this.countdown)}`;
      

      // Update the modal's countdown value and change message for the last 5 seconds
      if (activeModalRef.componentInstance) {
        activeModalRef.componentInstance.countdown = this.formatCountdown(this.countdown);
        //this.modalRef.componentInstance.autoLogoutMessage = updatedMessage; // Update message in modal
        activeModalRef.componentInstance.message = updatedMessage; // Update message in modal
      }
    } else {
      // Update the countdown without changing the message
      if (activeModalRef.componentInstance) {
        activeModalRef.componentInstance.countdown = this.formatCountdown(this.countdown);
      }
    }
    
  
      // Update the modal's countdown value dynamically
      if (activeModalRef.componentInstance) {
        activeModalRef.componentInstance.countdown = this.formatCountdown(this.countdown);
       
      }
  
      // Recursively call every second
      this.countdownTimeout = setTimeout(updateCountdown, 1000);
    };
  
    // Start the countdown
    updateCountdown();
  
    // Start auto-logout timer
    this.logoutTimeout = setTimeout(() => this.logoutUser(), this.warningDuration);
  
    try {
      const result = await activeModalRef.result; // Wait for user response
      clearTimeout(this.logoutTimeout); // Cancel auto-logout if user confirms
      clearTimeout(this.countdownTimeout);
      if (this.modalRef === activeModalRef) {
        this.modalRef = null;
      }
      if (result === 'confirmed') {
        this.resetTimer(); // Reset the timer if user stays logged in
      }
    } catch {
      clearTimeout(this.countdownTimeout);
      if (this.modalRef === activeModalRef) {
        this.modalRef = null;
      }
      this.logoutUser(); // Modal dismissed without interaction
    }
  }
  
  

  logoutAutoUser(): void {
    console.log('LogoutAutoUser called'); // Debugging line
    this.logoutFromButton();
  }

  private  logoutFromButton(){
    if (this.isLoggedOut) return; // Prevent multiple logout calls
    this.isLoggedOut = true;

    // Clear all timers
    clearTimeout(this.idleTimeout);
    clearTimeout(this.logoutTimeout);
    clearTimeout(this.countdownTimeout);

    // Remove event listeners
    this.eventListeners.forEach(event => {
      window.removeEventListener(event, this.resetTimerHandler);
    });

    // Close the modal if it's still open
    if (this.modalRef) {
      this.modalRef.dismiss(); // Close modal explicitly
      this.modalRef = null; // Clear the reference
    }

    // Close any other open modals
    this.modalService.closeAll();

    // Perform logout actions
    this.ngZone.run(async () => {
      this.toastService.clearAll();
      const companyCheck: string = localStorage.getItem("company") || 'default';
      localStorage.clear();
      sessionStorage.clear();
      await this.secureStorage.clearStorage();
      this.cookieService.deleteAll();
      localStorage.setItem("company", companyCheck);
      this.router.navigate(['/auth/login']); // Redirect to login page
    });
  }

  private async logoutUser(): Promise<void> {
    if (this.isLoggedOut) return; // Prevent multiple logout calls
    this.isLoggedOut = true;

    // Clear all timers
    clearTimeout(this.idleTimeout);
    clearTimeout(this.logoutTimeout);
    clearTimeout(this.countdownTimeout);

    // Remove event listeners
    this.eventListeners.forEach(event => {
      window.removeEventListener(event, this.resetTimerHandler);
    });

    // Close the modal if it's still open
    if (this.modalRef) {
      this.modalRef.dismiss(); // Close modal explicitly
      this.modalRef = null; // Clear the reference
    }

    // Close any other open modals
    this.modalService.closeAll();

    //await this.showLogoutPopup();

    // Perform logout actions
    this.ngZone.run(async () => {
      this.toastService.clearAll();
      const companyCheck: string = localStorage.getItem("company") || 'default';
      localStorage.clear();
      sessionStorage.clear();
      await this.secureStorage.clearStorage();
      this.cookieService.deleteAll();
      localStorage.setItem("company", companyCheck);
      this.router.navigate(['/auth/login']).then(() => {
        this.showLogoutPopup(); // Show the popup after navigation
      });
    });
  }

  // Show a new modal after auto logout
private async showLogoutPopup(): Promise<void> {
  const title = 'Session Timeout';
  const message = 'You have been logged out due to inactivity. Please log in again to continue.';

  // Show a modal to notify the user
  const logoutModalRef = this.modalService.openLogoutAlert(title, message);

  // Wait for the modal to be closed
  try {
    await logoutModalRef; // Wait for user interaction if needed
  } catch (error) {
    console.error('Error in logout modal:', error);
  } finally {
    // After the modal closes, proceed with the redirection
    this.router.navigate(['/auth/login']);
  }
}

  private formatCountdown(seconds: number): string {
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`;
  }
}
